import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createRiskOverviewSnapshotPublicationTransaction } from '../riskOverviewResource';

const UNAVAILABLE_MESSAGE = 'Factor position detail is unavailable. Risk data may have changed.';
const normalizeToken = (value) => (typeof value === 'string' ? value.trim() : '');
const snapshotChanged = (error) => (
    error?.status === 409 && error?.code === 'risk_overview_snapshot_changed'
);
const normalizeRows = (response) => {
    if (!Array.isArray(response?.positions) || response?.success === false) {
        throw new Error('Factor position detail response is invalid.');
    }
    return response.positions.map((row) => ({
        ...row,
        display_sedol: row?.display_sedol ?? row?.sedol ?? null,
        sedol: row?.display_sedol ?? row?.sedol ?? null,
    }));
};

export default function useFactorPositionDetails({
    scopeKey, snapshotToken, portfolioId, riskContext,
    fetchFactorPositions, fetchPositions, refreshRiskOverview,
    exclusiveCoreLoadOwnerRef = null,
}) {
    const [selectedFactor, setSelectedFactor] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestIdRef = useRef(0);
    const activeRef = useRef(null);
    const inputsRef = useRef(null);
    const currentScope = normalizeToken(scopeKey);
    const currentToken = normalizeToken(snapshotToken);
    inputsRef.current = {
        scopeKey: currentScope, snapshotToken: currentToken, portfolioId, riskContext,
        fetchFactorPositions, fetchPositions, refreshRiskOverview,
    };
    const lastSnapshotRef = useRef({ scopeKey: currentScope, snapshotToken: currentToken, portfolioId });

    const cancel = useCallback(() => {
        requestIdRef.current += 1;
        activeRef.current?.controller.abort();
        activeRef.current?.publication?.discard();
        activeRef.current = null;
    }, []);
    const reset = useCallback(() => {
        cancel();
        setSelectedFactor('');
        setRows([]);
        setLoading(false);
        setError(null);
    }, [cancel, exclusiveCoreLoadOwnerRef]);

    useLayoutEffect(() => {
        const previous = lastSnapshotRef.current;
        const changed = previous.scopeKey !== currentScope
            || previous.snapshotToken !== currentToken || previous.portfolioId !== portfolioId;
        const active = activeRef.current;
        const ownedPublication = active?.committed
            && active.scopeKey === currentScope && active.portfolioId === portfolioId
            && active.ownedSnapshotToken === currentToken;
        if (changed && !ownedPublication) reset();
        lastSnapshotRef.current = { scopeKey: currentScope, snapshotToken: currentToken, portfolioId };
    }, [currentScope, currentToken, portfolioId, reset]);
    useEffect(() => cancel, [cancel]);

    const selectFactor = useCallback(async (factorName) => {
        if (exclusiveCoreLoadOwnerRef?.current) return;
        cancel();
        const factor = normalizeToken(factorName);
        setSelectedFactor(factor);
        setRows([]);
        setError(null);
        setLoading(Boolean(factor));
        if (!factor) return;

        const inputs = inputsRef.current;
        const request = {
            id: ++requestIdRef.current,
            scopeKey: inputs.scopeKey, portfolioId: inputs.portfolioId,
            snapshotToken: inputs.snapshotToken, riskContext: { ...inputs.riskContext },
            controller: new AbortController(), publication: null,
            ownedSnapshotToken: null, committed: false,
        };
        activeRef.current = request;
        const isCurrent = () => {
            const latest = inputsRef.current;
            return !exclusiveCoreLoadOwnerRef?.current && activeRef.current === request && requestIdRef.current === request.id
                && latest.scopeKey === request.scopeKey && latest.portfolioId === request.portfolioId
                && (latest.snapshotToken === request.snapshotToken
                    || (request.committed && latest.snapshotToken === request.ownedSnapshotToken));
        };
        const fetchDetail = (token) => inputs.fetchFactorPositions({
            factorName: factor, snapshotToken: token, portfolioId: request.portfolioId,
            riskContextOverride: request.riskContext, signal: request.controller.signal,
        });

        try {
            if (!request.scopeKey || !request.snapshotToken || request.portfolioId == null) {
                throw new Error('A current risk snapshot is required.');
            }
            let response;
            try {
                response = await fetchDetail(request.snapshotToken);
            } catch (initialError) {
                if (!isCurrent() || initialError?.name === 'AbortError') return;
                if (!snapshotChanged(initialError)) throw initialError;

                const publication = createRiskOverviewSnapshotPublicationTransaction({
                    requiredPanels: ['positions', 'primary', 'group', 'detail'],
                    scopeKey: request.scopeKey, generation: request.id, isCurrent,
                });
                request.publication = publication;
                const options = {
                    isCurrentRequest: isCurrent, stagePublicationState: publication.stage,
                    generation: request.id, resourceScopeKey: request.scopeKey,
                    riskContextOverride: request.riskContext,
                };
                const positions = await inputs.fetchPositions(request.portfolioId, { ...options, silent: true });
                if (!isCurrent()) return;
                const refreshedToken = normalizeToken(positions?.risk_calculation_coverage?.snapshot_token);
                if (positions?.stale || !Array.isArray(positions?.positions) || !refreshedToken) {
                    throw new Error('Risk positions could not be refreshed.');
                }
                await inputs.refreshRiskOverview({ ...options, idioVolSource: request.riskContext.idioVolSource });
                if (!isCurrent()) return;
                response = await fetchDetail(refreshedToken);
                if (!isCurrent()) return;
                const refreshedRows = normalizeRows(response);
                publication.stage(() => setRows(refreshedRows), isCurrent, {
                    panel: 'detail', resource: response,
                    scopeKey: request.scopeKey, generation: request.id,
                });
                if (publication.willPublishFailure() || !publication.canCommit()) {
                    throw new Error('Risk snapshot changed during refresh.');
                }
                request.ownedSnapshotToken = refreshedToken;
                // React applies the staged state setters after this synchronous
                // commit, so only this exact token change may retain selection.
                request.committed = true;
                if (!publication.commit()) {
                    request.committed = false;
                    throw new Error('Risk snapshot could not be published.');
                }
                return;
            }
            if (isCurrent()) setRows(normalizeRows(response));
        } catch (failure) {
            request.publication?.discard();
            if (failure?.name !== 'AbortError' && isCurrent()) setError(UNAVAILABLE_MESSAGE);
        } finally {
            if (!request.committed) request.publication?.discard();
            if (isCurrent()) setLoading(false);
        }
    }, [cancel]);

    return { selectedFactor, rows, loading, error, selectFactor, reset };
}
